import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Form,
  Input,
  Select,
  Switch,
  Space,
  Tag,
  Tooltip,
  Typography,
  Alert,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  KeyOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Button, Modal, Badge, PageHeader } from "../../components/Common";
import PaginatedTable from "../../components/PaginatedTable";
import SearchSelect from "../../components/SearchSelect";
import { useMutations } from "../../hooks/useApiMutation";
import { useConfirm } from "../../hooks/useConfirm";
import useAccess, { ROLES, MODULES } from "../../hooks/useAccess";
import CredentialsModal from "./CredentialsModal";
import PermissionsDrawer from "./PermissionsDrawer";

const { Text } = Typography;

const ROLE_COLOR = {
  Owner: "magenta",
  Admin: "red",
  Developer: "blue",
  Client: "green",
};

/**
 * Account administration: create developer and client logins, set what a developer
 * can reach, and hand out temporary passwords.
 */
const UsersList = () => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [permissionsFor, setPermissionsFor] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null);
  const [form] = Form.useForm();
  const role = Form.useWatch("role", form);

  // PaginatedTable re-fetches whenever this object's JSON changes, so it must not be
  // rebuilt inline on every render.
  const filters = useMemo(() => ({ role: roleFilter }), [roleFilter]);

  const { mutate, loading } = useMutations();
  const { confirmAction } = useConfirm();
  const { can, isUnrestricted } = useAccess();
  const tableRef = useRef(null);

  const canCreate = can(MODULES.USERS, "create");
  const canUpdate = can(MODULES.USERS, "update");
  const canDelete = can(MODULES.USERS, "delete");

  const fetchData = useCallback(
    async (page, pageSize, search, activeFilters) => {
      let result = { data: [], total: 0 };
      await mutate("fetchUsers", {
        method: "get",
        endpoint: "/users",
        params: { page, pageSize, search, role: activeFilters?.role || undefined },
        onSuccess: (res) => {
          result = { data: res?.items || [], total: res?.totalCount || 0 };
        },
        onError: () => {
          result = { data: [], total: 0 };
        },
      });
      return result;
    },
    [mutate]
  );

  const columns = [
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          {r.displayName && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {r.displayName}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (v) => <Tag color={ROLE_COLOR[v] || "default"}>{v}</Tag>,
    },
    {
      title: "Client",
      dataIndex: "companyName",
      key: "companyName",
      render: (v) => v || <Text type="secondary">—</Text>,
      responsive: ["md"],
    },
    { title: "Email", dataIndex: "email", key: "email", responsive: ["lg"] },
    {
      title: "Status",
      key: "status",
      render: (_, r) => (
        <Space size={4}>
          <Badge variant={r.isActive ? "success" : "error"}>
            {r.isActive ? "Active" : "Disabled"}
          </Badge>
          {r.mustChangePassword && (
            <Tooltip title="Still using the temporary password it was created with">
              <Tag color="gold">Temp password</Tag>
            </Tooltip>
          )}
          {r.twoFactorEnabled && (
            <Tooltip title="Signs in with an authenticator app">
              <Tag color="geekblue" icon={<SafetyOutlined />}>
                2FA
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Last login",
      dataIndex: "lastLoginUtc",
      key: "lastLoginUtc",
      render: (v) => (v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—"),
      responsive: ["lg"],
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          {record.role === ROLES.DEVELOPER && canUpdate && (
            <Tooltip title="Module access">
              <Button
                variant="ghost"
                icon={<SafetyCertificateOutlined />}
                onClick={() => setPermissionsFor(record)}
              />
            </Tooltip>
          )}
          {canUpdate && (
            <Tooltip title="Edit">
              <Button
                variant="ghost"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditing(record);
                  form.setFieldsValue({
                    email: record.email,
                    displayName: record.displayName,
                    isActive: record.isActive,
                    role: record.role,
                    companyId: record.companyId,
                  });
                  setOpen(true);
                }}
              />
            </Tooltip>
          )}
          {canUpdate && (
            <Tooltip title="Reset password">
              <Button
                variant="ghost"
                icon={<KeyOutlined />}
                onClick={() =>
                  confirmAction({
                    key: "resetUserPassword",
                    method: "post",
                    endpoint: `/users/${record.id}/reset-password`,
                    title: "Reset password",
                    content: `Issue a new temporary password for "${record.username}"? Their active sessions are signed out.`,
                    okText: "Reset",
                    onSuccess: (data) => {
                      setCredentials(data);
                      tableRef.current?.reloadPage();
                    },
                  })
                }
              />
            </Tooltip>
          )}
          {canUpdate && record.twoFactorEnabled && (
            <Tooltip title="Reset two-step verification">
              <Button
                variant="ghost"
                icon={<SafetyOutlined />}
                onClick={() =>
                  confirmAction({
                    key: "resetUserTwoFactor",
                    method: "post",
                    endpoint: `/users/${record.id}/2fa/reset`,
                    title: "Reset two-step verification",
                    content: `Remove the authenticator enrolled for "${record.username}"? They will sign in with their password alone until they enrol a new device, and their active sessions are signed out. Confirm their identity out of band first.`,
                    okText: "Reset",
                    okType: "danger",
                    onSuccess: () => tableRef.current?.reloadPage(),
                  })
                }
              />
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete">
              <Button
                variant="ghost"
                danger
                icon={<DeleteOutlined />}
                onClick={() =>
                  confirmAction({
                    key: "deleteUser",
                    method: "delete",
                    endpoint: `/users/${record.id}`,
                    title: "Delete user",
                    content: `Delete "${record.username}"? This cannot be undone.`,
                    okText: "Delete",
                    okType: "danger",
                    onSuccess: () => tableRef.current?.reloadPage(),
                  })
                }
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const onCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ role: ROLES.DEVELOPER, isActive: true, sendEmail: true });
    setOpen(true);
  };

  const handleOk = async () => {
    const values = await form.validateFields();

    if (editing) {
      await mutate("updateUser", {
        method: "put",
        endpoint: `/users/${editing.id}`,
        payload: {
          email: values.email,
          displayName: values.displayName,
          isActive: values.isActive,
          role: values.role,
          companyId: values.role === ROLES.CLIENT ? values.companyId : null,
        },
        onSuccess: () => {
          setOpen(false);
          tableRef.current?.reloadPage();
        },
      });
      return;
    }

    await mutate("createUser", {
      method: "post",
      endpoint: "/users",
      payload: {
        username: values.username,
        role: values.role,
        email: values.email,
        displayName: values.displayName,
        companyId: values.role === ROLES.CLIENT ? values.companyId : null,
        isActive: values.isActive,
        sendEmail: values.sendEmail,
        permissions: [],
      },
      onSuccess: (data) => {
        setOpen(false);
        setCredentials(data);
        tableRef.current?.reloadPage();
      },
    });
  };

  return (
    <div>
      <PageHeader
        title="Users & access"
        subtitle="Manage back-office accounts and per-module developer permissions."
        extra={
          canCreate && (
            <Button variant="primary" icon={<PlusOutlined />} onClick={onCreate}>
              New user
            </Button>
          )
        }
      />

      <Space style={{ marginBottom: 12 }}>
        <Select
          allowClear
          placeholder="All roles"
          style={{ width: 200 }}
          value={roleFilter}
          onChange={setRoleFilter}
          options={[
            { value: ROLES.OWNER, label: "Owner" },
            { value: ROLES.ADMIN, label: "Admin" },
            { value: ROLES.DEVELOPER, label: "Developer" },
            { value: ROLES.CLIENT, label: "Client" },
          ]}
        />
      </Space>

      <PaginatedTable
        ref={tableRef}
        columns={columns}
        fetchData={fetchData}
        filters={filters}
        rowKey="id"
      />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={handleOk}
        title={editing ? `Edit ${editing.username}` : "New user"}
        confirmText={editing ? "Save" : "Create"}
        loading={loading?.createUser || loading?.updateUser}
      >
        <Form form={form} layout="vertical">
          {!editing && (
            <>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="A temporary password is generated automatically and shown once after creation."
              />
              <Form.Item
                name="username"
                label="Username"
                rules={[{ required: true, message: "Username is required" }]}
              >
                <Input placeholder="e.g. arben.dev" />
              </Form.Item>
            </>
          )}

          {/* Role and tenant are editable after creation too: changing the role signs
              the account out and, when it stops being a developer, clears its module
              grants — so the change is announced rather than silent. */}
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select
              options={[
                { value: ROLES.DEVELOPER, label: "Developer — back-office, scoped to modules" },
                { value: ROLES.CLIENT, label: "Client — portal only, tied to one company" },
                ...(isUnrestricted
                  ? [
                      { value: ROLES.ADMIN, label: "Admin — unrestricted back-office" },
                      { value: ROLES.OWNER, label: "Owner — unrestricted back-office" },
                    ]
                  : []),
              ]}
            />
          </Form.Item>

          {role === ROLES.CLIENT && (
            <Form.Item
              name="companyId"
              label="Client company"
              rules={[{ required: true, message: "A client account must belong to a company" }]}
            >
              <SearchSelect
                fetchKey="usersClientSearch"
                endpoint="/clients/search"
                labelKey="name"
                valueKey="companyId"
                placeholder="Search clients"
              />
            </Form.Item>
          )}

          {editing && role !== editing.role && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`Changing the role from ${editing.role} to ${role}`}
              description={
                editing.role === ROLES.DEVELOPER
                  ? "Their module permissions will be removed and any active session signed out."
                  : "Any active session will be signed out."
              }
            />
          )}

          <Form.Item name="displayName" label="Display name">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: "email" }]}>
            <Input placeholder="Where the temporary password is sent" />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          {!editing && (
            <Form.Item
              name="sendEmail"
              label="Email the credentials"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <CredentialsModal
        credentials={credentials}
        onClose={() => setCredentials(null)}
      />

      <PermissionsDrawer
        user={permissionsFor}
        onClose={() => setPermissionsFor(null)}
        onSaved={() => tableRef.current?.reloadPage()}
      />
    </div>
  );
};

export default UsersList;
